import { Box, DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import {
  PageHeaderWrapper,
  Tab,
  Tabs,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useState } from "react";

import { CancellationProtectionTermsList } from "./CancellationProtectionTermsList";

interface CancellationProtectionTermsDialogProps {
  modalState: UseModalState;
}

const TABS = [{ label: "Terms", value: "terms" }] as const;

type TabValue = (typeof TABS)[number]["value"];

function isValidTabValue(value: string): value is TabValue {
  return TABS.some((tab) => tab.value === value);
}

export function CancellationProtectionTermsDialog(props: CancellationProtectionTermsDialogProps) {
  const { modalState } = props;
  const [activeTab, setActiveTab] = useState<TabValue>("terms");

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow onClickBack={modalState.closeModal} />
      </PageHeaderWrapper>

      <Box>
        <Stack spacing={2} sx={{ paddingBottom: 8, paddingX: 5 }}>
          <Title
            component="h1"
            variant="h3"
            sx={(theme) => ({ color: theme.palette.text.primary })}
          >
            Cancellation Protection
          </Title>
          <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
            Know that your time is always valued and your earnings protected when a workplace
            cancels you last minute
          </Text>
        </Stack>

        <Tabs
          variant="underlined"
          value={activeTab}
          sx={{
            "& .MuiTabs-flexContainer": { gap: 6, paddingX: 5 },
            "& .MuiTab-root": {
              flexGrow: 0,
              paddingX: 0,
              minWidth: "auto",
            },
          }}
          onChange={(_, value: string) => {
            if (isValidTabValue(value)) {
              setActiveTab(value);
            } else {
              setActiveTab("terms");
            }
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.value}
              variant="underlined"
              label={
                <Text bold variant="body1" sx={(theme) => ({ color: theme.palette.text.primary })}>
                  {tab.label}
                </Text>
              }
              value={tab.value}
            />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Stack sx={{ paddingY: 8 }}>
          {activeTab === "terms" ? <CancellationProtectionTermsList variant="overview" /> : null}
        </Stack>
      </DialogContent>
    </FullScreenDialog>
  );
}
